import { showError } from '@calendly/alert';
import { useBooleanState, useOnValueChange } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { Body, Footer, Header, Popup, Title } from '@calendly/ui/components/popup';
import { SimpleSelect } from '@calendly/ui/components/select';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, useState, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { getBuffersOptions } from 'components/event_type_edit';
import { useUpdateBufferMutation } from '../../api';
const popupContainerCss = "p13iajl7";
const PopupContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(popupContainerCss, props.className)} />;
});
const closeButtonIsPhoneCss = "c4j8kpm";
const closeButtonCss = "c8iux2z";
const CloseButton = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <IconButton ref={ref} {...props} className={classNames(closeButtonCss, {
    [closeButtonIsPhoneCss]: isPhone
  }, props.className)} />;
});
const subheaderCss = "so48yke";
const Subheader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(subheaderCss, props.className)} />;
});
const editingBufferTextCss = "e5nftp8";
const EditingBufferText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(editingBufferTextCss, props.className)} />;
});
const bufferInputContainerCss = "bgvvqd1";
const BufferInputContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bufferInputContainerCss, props.className)} />;
});
const styledBufferInputCss = "s4x8rf0";
const StyledBufferInput = forwardRef((props, ref) => <SimpleSelect ref={ref} {...props} className={classNames(styledBufferInputCss, props.className)} />);
export const EditBufferPopup = ({
  eventId,
  isMultiHost,
  eventName,
  isBefore,
  userMembership,
  isOpen,
  onClose
}) => {
  const dispatch = useDispatch();
  const [isConfirmationOpen, openConfirmation, closeConfirmation] = useBooleanState(false);
  const {
    beforeBufferTime,
    afterBufferTime
  } = userMembership || {};
  const [bufferDuration, setBufferDuration] = useState(isBefore ? beforeBufferTime : afterBufferTime);
  const bufferOptions = getBuffersOptions();
  const [updateBufferFn, {
    isLoading: isUpdating,
    isSuccess,
    error
  }] = useUpdateBufferMutation({
    fixedCacheKey: 'shared-update-buffer'
  });
  const overlapError = error?.type === 'validation' ? error.payload.base[0] : null;
  useEffect(() => {
    setBufferDuration(isBefore ? beforeBufferTime : afterBufferTime);
  }, [isBefore, beforeBufferTime, afterBufferTime, setBufferDuration]);
  useOnValueChange(isSuccess, () => {
    if (isSuccess) onClose();
  });
  useOnValueChange(overlapError, () => {
    if (overlapError) {
      dispatch(showError(overlapError));
    }
  });
  const updateBuffer = updatedDuration => {
    const currentDuration = isBefore ? beforeBufferTime : afterBufferTime;
    if (updatedDuration === '0') {
      openConfirmation();
      return;
    }
    if (currentDuration === updatedDuration) {
      onClose();
      return;
    }
    updateBufferFn({
      eventId,
      membershipId: userMembership.id,
      isBefore,
      duration: updatedDuration
    });
  };
  if (!isOpen) return null;
  if (isConfirmationOpen) {
    return <Popup isOpen={isOpen} onClose={closeConfirmation}>
        <PopupContainer>
          <Header>
            <Title>{I18n.t('publisher.scheduled_events.buffers.confirm_deletion_modal.title')}</Title>
          </Header>
          <Body>
            {I18n.t('publisher.scheduled_events.buffers.confirm_deletion_modal.body')}
          </Body>
          <Footer>
            <Button decoration="secondary-outline" onClick={closeConfirmation}>
              {I18n.t('publisher.action.cancel')}
            </Button>
            <Button decoration="danger" onClick={() => updateBuffer(0)} loading={isUpdating}>
              {I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.delete_buffer')}
            </Button>
          </Footer>
          <CloseButton title={I18n.t('publisher.action.close')} onClick={closeConfirmation}>
            <CloseIcon />
          </CloseButton>
        </PopupContainer>
      </Popup>;
  }
  return <Popup isOpen={isOpen} onClose={onClose}>
      <PopupContainer>
        <Header>
          {isBefore && <Title>{I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.title.edit_before_buffer')}</Title>}
          {!isBefore && <Title>{I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.title.edit_after_buffer')}</Title>}
        </Header>
        <Subheader>
          {I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.subtitle')}
        </Subheader>
        <Body>
          <EditingBufferText>
            {isMultiHost ? HtmlParser(I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.description.multi_host', {
            event_name: eventName
          })) : HtmlParser(I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.description.single_host', {
            event_name: eventName
          }))}
          </EditingBufferText>
          <BufferInputContainer>
            <div id="buffer_label">
              {I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.buffer.title')}
              :
            </div>
            <StyledBufferInput value={bufferDuration} options={bufferOptions} name="buffer" aria-labelledby="buffer_label" onChange={value => setBufferDuration(value)} />
          </BufferInputContainer>
        </Body>
        <Footer>
          <Button decoration="danger" onClick={openConfirmation}>
            {I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.delete_buffer')}
          </Button>
          <Button onClick={() => updateBuffer(bufferDuration)} loading={isUpdating}>
            {I18n.t('publisher.organization.reporting.popular_times_setting.confirm')}
          </Button>
        </Footer>
        <CloseButton title={I18n.t('publisher.action.close')} onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </PopupContainer>
    </Popup>;
};

require("./edit_buffer_popup.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./edit_buffer_popup.js");